import React, { useMemo } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { AssociationSubPage } from '@/components/accounting/association-sub-page';
import { ReportActionBar } from '@/components/reports/report-action-bar';
import type {
	TreeExportRow,
	TreeReport,
	TreeReportDefinition,
	TreeRow,
} from '@/types/report-definition';
import { type ExportColumn, exportRowsToCsv, exportRowsToXlsx } from '@/utils/accounting-export';
import { formatMoneyCents } from '@/utils/format-money';

export interface ReportViewTreeProps<TData> {
	/** Definition for this report — title, subtitle, buildTree, printUrl, buildExport. */
	definition: TreeReportDefinition<TData>;
	/** The loaded payload (Balance Sheet / P&L response). When null the empty state shows. */
	data: TData | null | undefined;
	/** Association the report is scoped to. */
	associationId: string;
	associationName?: string;
	loading?: boolean;
	error?: Error | null;
	/** Slot for the page's filter UI — date pickers, basis chips, etc. */
	filters?: React.ReactNode;
	/** Extra buttons rendered alongside Print / Excel. */
	extraActions?: React.ReactNode;
	/** Params passed through to `definition.printUrl`. */
	printParams?: Record<string, unknown>;
	excelFilename?: string;
	emptyMessage?: string;
	/**
	 * When set, account-detail rows (those with `glCodeId` and not headings)
	 * become clickable. The page typically renders a GL detail modal in
	 * response. Heading / subtotal / grand-total / synthetic rows stay inert.
	 */
	onAccountClick?: (row: TreeRow) => void;
	/**
	 * Generic row-press hook for non-GL tree reports (e.g. 1099 Summary
	 * drilling into the vendor). Fires for any non-heading row that carries
	 * `id`. Heading / subtotal rows stay inert.
	 */
	onRowPress?: (row: TreeRow) => void;
	debugSqlPath?: string;
}

/**
 * Sectioned report view for Balance Sheet / P&L. Wraps `AssociationSubPage`
 * with the standard chrome, exposes Print + Excel actions (no Customize —
 * tree columns are fixed), and renders the tree returned by
 * `definition.buildTree`. One or two columns; optional grand totals; optional
 * net-line below all columns (used by P&L for Net Income / Net Loss).
 */
export const ReportViewTree = <TData,>(props: ReportViewTreeProps<TData>): React.ReactElement => {
	const {
		definition,
		data,
		associationId,
		associationName,
		loading = false,
		error,
		filters,
		extraActions,
		printParams,
		excelFilename,
		emptyMessage = 'No data available for this period.',
		onAccountClick,
		onRowPress,
		debugSqlPath,
	} = props;

	const tree = useMemo<TreeReport | null>(
		() => (data ? definition.buildTree(data) : null),
		[data, definition],
	);

	const hasContent = Boolean(tree) && tree!.columns.some((col) => col.sections.some((s) => s.rows.length > 0));
	const exportDisabled = loading || !hasContent;

	const exportFilename = excelFilename ?? `${definition.id}-${Date.now()}`;

	const resolveExport = (): { columns: ExportColumn<TreeExportRow>[]; rows: TreeExportRow[] } => {
		if (data && definition.buildExport) {
			const built = definition.buildExport(data);
			return {
				columns: built.columns.map((c) => ({
					header: c.header,
					value: c.accessor,
					format: c.format,
					width: c.width,
				})),
				rows: built.rows,
			};
		}
		return defaultTreeExport(tree);
	};

	const onExcel = async () => {
		const ex = resolveExport();
		await exportRowsToXlsx(exportFilename, ex.columns, ex.rows);
	};

	const onCsv = async () => {
		const ex = resolveExport();
		await exportRowsToCsv(exportFilename, ex.columns, ex.rows);
	};

	const onPrint = () => {
		if (Platform.OS === 'web' && definition.printUrl) {
			const url = definition.printUrl(associationId, printParams ?? {});
			window.open(url, '_blank');
		}
	};

	const actionBar = (
		<>
			{extraActions}
			<ReportActionBar
				onPrint={onPrint}
				onExcel={onExcel}
				onCsv={onCsv}
				disabled={exportDisabled}
				debugSqlPath={debugSqlPath}
				debugReportTitle={definition.title}
			/>
		</>
	);

	return (
		<AssociationSubPage
			associationId={associationId}
			associationName={associationName}
			title={definition.title}
			subtitle={definition.subtitle}
			right={actionBar}
		>
			{filters}
			{error ? (
				<Text style={styles.warn}>Failed to load report: {error.message}</Text>
			) : loading && !tree ? (
				<View style={styles.center}><ActivityIndicator /></View>
			) : !tree || !hasContent ? (
				<Text style={styles.empty}>{emptyMessage}</Text>
			) : (
				<TreeBody tree={tree} onAccountClick={onAccountClick} onRowPress={onRowPress} />
			)}
		</AssociationSubPage>
	);
};

export const TreeBody: React.FC<{
	tree: TreeReport;
	onAccountClick?: (row: TreeRow) => void;
	onRowPress?: (row: TreeRow) => void;
}> = ({ tree, onAccountClick, onRowPress }) => {
	const isTChart = tree.columns.length > 1;
	// Two-amount-column mode kicks in when ANY row, total, or net-line carries
	// `ytdAmount`. We treat the whole tree as YTD-aware so headers + alignment
	// stay consistent across sections.
	const hasYtd = (
		tree.columns.some((c) =>
			c.sections.some((s) =>
				s.rows.some((r) => r.ytdAmount !== undefined)
				|| s.total?.ytdAmount !== undefined,
			),
		)
		|| tree.netLine?.ytdAmount !== undefined
	);
	return (
		<View style={styles.card}>
			<View style={isTChart ? styles.tchart : styles.singleCol}>
				{tree.columns.map((col, idx) => (
					<React.Fragment key={col.title ?? idx}>
						<View style={styles.col}>
							{col.title ? <Text style={styles.colHead}>{col.title}</Text> : null}
							{hasYtd ? (
								<View style={styles.amtHeaderRow}>
									<Text style={styles.amtHeaderSpacer} />
									<Text style={styles.amtHeaderCell}>{tree.amountColumnLabels?.primary ?? ''}</Text>
									<Text style={styles.amtHeaderCell}>{tree.amountColumnLabels?.secondary ?? ''}</Text>
								</View>
							) : null}
							{col.sections.map((section) => (
								<View key={section.id} style={styles.section}>
									<Text style={styles.sectionLabel}>{section.title}</Text>
									{section.rows.length === 0 ? (
										<Text style={styles.sectionEmpty}>
											{section.emptyMessage ?? 'No entries in this section.'}
										</Text>
									) : section.rows.map((row, rIdx) => (
										<TreeRowView
											key={row.id ?? `${section.id}-${rIdx}`}
											row={row}
											showYtd={hasYtd}
											onAccountClick={onAccountClick}
											onRowPress={onRowPress}
										/>
									))}
									{section.total ? (
										<View style={styles.totalRow}>
											<Text style={styles.totalLabel}>{section.total.label}</Text>
											<Text style={styles.totalAmt}>{formatMoneyCents(section.total.amount)}</Text>
											{hasYtd ? (
												<Text style={styles.totalAmt}>
													{section.total.ytdAmount !== undefined ? formatMoneyCents(section.total.ytdAmount) : ''}
												</Text>
											) : null}
										</View>
									) : null}
								</View>
							))}
							{col.grandTotal ? (
								<>
									<View style={styles.grandDivider} />
									<View style={styles.grandRow}>
										<Text style={styles.grandLabel}>{col.grandTotal.label}</Text>
										<Text style={styles.grandAmt}>{formatMoneyCents(col.grandTotal.amount)}</Text>
									</View>
								</>
							) : null}
						</View>
						{isTChart && idx < tree.columns.length - 1 ? <View style={styles.divider} /> : null}
					</React.Fragment>
				))}
			</View>

			{tree.netLine ? (
				<View style={[styles.netBox, tree.netLine.isLoss && styles.netBoxLoss]}>
					<Text style={[styles.netLabel, tree.netLine.isLoss && styles.netTextLoss]}>
						{tree.netLine.label}
					</Text>
					<Text style={[styles.netAmt, tree.netLine.isLoss && styles.netTextLoss]}>
						{formatMoneyCents(tree.netLine.amount)}
					</Text>
					{hasYtd ? (
						<Text style={[styles.netAmt, tree.netLine.isLoss && styles.netTextLoss]}>
							{tree.netLine.ytdAmount !== undefined ? formatMoneyCents(tree.netLine.ytdAmount) : ''}
						</Text>
					) : null}
				</View>
			) : null}

			{tree.note ? <Text style={styles.note}>{tree.note}</Text> : null}
		</View>
	);
};

const TreeRowView: React.FC<{
	row: TreeRow;
	showYtd?: boolean;
	onAccountClick?: (row: TreeRow) => void;
	onRowPress?: (row: TreeRow) => void;
}> = ({ row, showYtd, onAccountClick, onRowPress }) => {
	const indent = row.indent ?? 0;
	const isSubtotal = row.tone === 'subtotal';
	const isUncat = row.tone === 'uncategorized';
	const isHeading = row.heading === true;
	const glClickable = !!onAccountClick && !!row.glCodeId && !isHeading;
	const rowClickable = !glClickable && !!onRowPress && !!row.id && !isHeading && !isSubtotal;
	const isClickable = glClickable || rowClickable;
	const handlePress = glClickable ? () => onAccountClick!(row) : () => onRowPress!(row);
	const labelStyles = [
		styles.rowLabel,
		indent > 0 && { paddingLeft: 16 * indent },
		(isSubtotal || isHeading) && styles.rowBold,
		isUncat && styles.rowUncat,
		isClickable && styles.rowLabelLink,
	];
	const codeStyles = [styles.rowCode, isClickable && styles.rowCodeLink];
	const amtStyles = [styles.rowAmt, isSubtotal && styles.rowBold];

	const body = (
		<View style={[styles.row, isClickable && styles.rowClickable]}>
			{row.code ? <Text style={codeStyles}>{row.code}</Text> : null}
			<Text style={labelStyles} numberOfLines={1}>
				{row.label}{isUncat ? ' (uncategorized)' : ''}
			</Text>
			{!showYtd && typeof row.pct === 'number' ? (
				<Text style={styles.rowPct}>{row.pct.toFixed(1)}%</Text>
			) : null}
			{isHeading ? <Text style={amtStyles} /> : (
				<Text style={amtStyles}>{formatMoneyCents(row.amount)}</Text>
			)}
			{showYtd ? (
				isHeading ? <Text style={amtStyles} /> : (
					<Text style={amtStyles}>
						{row.ytdAmount !== undefined ? formatMoneyCents(row.ytdAmount) : ''}
					</Text>
				)
			) : null}
		</View>
	);

	if (!isClickable) return body;

	return (
		<Pressable
			onPress={handlePress}
			accessibilityRole="button"
			accessibilityLabel={`Open detail for ${row.code ? `${row.code} ` : ''}${row.label}`}
		>
			{body}
		</Pressable>
	);
};

/**
 * Default flat export for tree reports. Each TreeRow becomes one row with
 * Section / Code / Account / Amount. Subtotal rows are emitted; grand totals
 * and net-lines append at the end so the file is self-contained.
 */
function defaultTreeExport(tree: TreeReport | null): { columns: ExportColumn<TreeExportRow>[]; rows: TreeExportRow[] } {
	const columns: ExportColumn<TreeExportRow>[] = [
		{ header: 'Section', value: (r) => r.section, width: 160 },
		{ header: 'Code', value: (r) => r.code, width: 80 },
		{ header: 'Account', value: (r) => r.account, width: 240 },
		{ header: 'Amount', value: (r) => r.amount, format: 'money', width: 120 },
	];
	if (!tree) return { columns, rows: [] };

	const rows: TreeExportRow[] = [];
	for (const col of tree.columns) {
		for (const section of col.sections) {
			const sectionTitle = col.title ? `${col.title} · ${section.title}` : section.title;
			for (const r of section.rows) {
				if (r.heading) continue;
				rows.push({
					section: sectionTitle,
					code: r.code ?? '',
					account: '  '.repeat(r.indent ?? 0) + r.label,
					amount: r.amount,
				});
			}
			if (section.total) {
				rows.push({ section: sectionTitle, code: '', account: section.total.label, amount: section.total.amount });
			}
		}
		if (col.grandTotal) {
			rows.push({
				section: col.title ?? '',
				code: '',
				account: col.grandTotal.label,
				amount: col.grandTotal.amount,
			});
		}
	}
	if (tree.netLine) {
		rows.push({ section: '', code: '', account: tree.netLine.label, amount: tree.netLine.amount });
	}
	return { columns, rows };
}

const styles = StyleSheet.create({
	card: {
		backgroundColor: '#fff',
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#e0e7ef',
		padding: 16,
	},
	tchart: { flexDirection: 'row', alignItems: 'stretch' },
	singleCol: { flexDirection: 'column' },
	col: { flex: 1, padding: 8, minWidth: 0 },
	colHead: {
		fontSize: 14, fontWeight: '700', textAlign: 'center',
		textTransform: 'uppercase', letterSpacing: 1,
		marginBottom: 12, paddingBottom: 8,
		borderBottomWidth: 1, borderColor: '#90a4ae',
	},
	divider: { width: 1, backgroundColor: '#cfd8dc' },

	amtHeaderRow: {
		flexDirection: 'row', alignItems: 'flex-end', gap: 8,
		borderBottomWidth: 1, borderColor: '#cfd8dc',
		paddingBottom: 4, marginBottom: 8,
	},
	amtHeaderSpacer: { flex: 1, fontSize: 11 },
	amtHeaderCell: {
		width: 120, fontSize: 11, fontWeight: '700', color: '#37474f',
		textAlign: 'right', textTransform: 'uppercase', letterSpacing: 0.4,
	},

	section: { marginBottom: 14 },
	sectionLabel: {
		fontSize: 12, fontWeight: '700', color: '#37474f',
		marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5,
	},
	sectionEmpty: { fontSize: 12, color: '#888', fontStyle: 'italic', paddingVertical: 4 },

	row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3, gap: 8 },
	rowClickable: {
		...Platform.select({
			web: { cursor: 'pointer' } as object,
			default: {},
		}),
		borderRadius: 4,
	},
	rowCode: { width: 70, fontSize: 12, color: '#455a64', fontVariant: ['tabular-nums'] },
	rowCodeLink: { color: '#1976d2', fontWeight: '600' },
	rowLabel: { flex: 1, fontSize: 13, color: '#222', minWidth: 0 },
	rowLabelLink: { color: '#1976d2' },
	rowPct: { width: 56, fontSize: 12, color: '#546e7a', textAlign: 'right', fontVariant: ['tabular-nums'] },
	rowAmt: { width: 120, fontSize: 13, color: '#222', textAlign: 'right', fontVariant: ['tabular-nums'] },
	rowBold: { fontWeight: '700' },
	rowUncat: { color: '#c62828', fontStyle: 'italic' },

	totalRow: {
		flexDirection: 'row', alignItems: 'center', gap: 8,
		paddingTop: 4, marginTop: 4,
		borderTopWidth: 1, borderColor: '#90a4ae',
	},
	totalLabel: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: '700', color: '#000' },
	totalAmt: { width: 120, fontSize: 13, fontWeight: '700', color: '#000', textAlign: 'right', fontVariant: ['tabular-nums'] },

	grandDivider: { borderTopWidth: 2, borderColor: '#000', marginVertical: 6 },
	grandRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 },
	grandLabel: { flex: 1, minWidth: 0, fontSize: 14, fontWeight: '700', color: '#000' },
	grandAmt: { width: 120, fontSize: 14, fontWeight: '700', color: '#000', textAlign: 'right', fontVariant: ['tabular-nums'] },

	netBox: {
		flexDirection: 'row', alignItems: 'center', gap: 8,
		paddingHorizontal: 12, paddingVertical: 10,
		borderWidth: 2, borderColor: '#000', backgroundColor: '#eceff1',
		marginTop: 8, borderRadius: 4,
	},
	netBoxLoss: { backgroundColor: '#fdecea', borderColor: '#c62828' },
	netLabel: { flex: 1, minWidth: 0, fontSize: 16, fontWeight: '700', color: '#000', textTransform: 'uppercase', letterSpacing: 1 },
	netAmt: { width: 120, fontSize: 16, fontWeight: '700', color: '#000', textAlign: 'right', fontVariant: ['tabular-nums'] },
	netTextLoss: { color: '#c62828' },

	note: { marginTop: 12, fontSize: 12, color: '#78909c', fontStyle: 'italic', textAlign: 'center' },
	empty: { color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 24 },
	warn: { color: '#c62828', fontSize: 13 },
	center: { padding: 32, alignItems: 'center' },
});

export default ReportViewTree;
